/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/**
 * totaalexport.ts
 * 20-05-2024 - Jelmer Jellema - Spin in het Web B.V. Apeldoorn NL
 * Component Totaalexportscherm
 * Selector: totaalexport
 */
import {Component, OnInit, inject} from '@angular/core';
import {Appscherm} from '../abstracts/appscherm';
import {Planitem} from '../../models/planitem';
import {PersoneelPlanitem} from '../../models/personeel_planitem';
import {Functie} from '../../models/functie';
import {hash} from '../../app/types';
import {Personeel} from '../../models/personeel';
import {Project} from '../../models/project';
import {Leverancier} from '../../models/leverancier';
import {Werkmaatschappij} from '../../models/werkmaatschappij';
import {sortBy} from 'lodash-es';
import * as Excel from 'exceljs';
import {FileSaverService} from 'ngx-filesaver';
import moment from 'moment';
import {FiltertoolFactoryService} from '../../services/filtertool/filtertool-factory.service';
import {FiltertoolInstantie} from '../../services/filtertool/filtertool-instantie';
import {PersoneelFunctie} from '../../models/personeel_functie';
import {PersoneelLabel} from '../../models/personeel_label';

/**
 * UI‑state voor het Totaalexportscherm.
 * Bevat instellingen die uit de template worden gebonden.
 */
interface TotaalexportUx {
  /** Schakelt het toepassen van filters aan/uit. */
  filterActief: boolean;
  /** Het gekozen exportbereik als [van, tot] datumwaarden (inclusief), of null wanneer niet ingesteld. */
  exportDaterange?: [Date, Date] | null;
}

@Component({
  selector: 'totaalexport',
  templateUrl: './totaalexport.html',
  styleUrls: ['totaalexport.scss']
})
export class Totaalexportscherm extends Appscherm implements OnInit {
  _starttitle = 'Totaalexport';
  icon = 'fas fa-file-export';
/*  fixedHeight = 250;
  fixedWidth = 300;
  fixedSize = true;*/

  /**
   * UI‑state voor dit scherm. De filtertoepassing staat standaard aan, conform Planbord.
   */
  public ux: TotaalexportUx = {
    filterActief: true,
    exportDaterange: null
  };

  /**
   * De filtertool-factory. Hiermee wordt de contextgebonden filterinstantie opgehaald en opgeslagen.
   */
  private readonly filtertoolFactory = inject(FiltertoolFactoryService);
  /**
   * De geregistreerde filtertool-instantie die bij dit totaalexportscherm hoort.
   */
  public filtertool: FiltertoolInstantie = null;
  /**
   * Vlag die aangeeft of filteren toegestaan is voor de huidige gebruiker. Alles-of-niets.
   */
  public magFilteren: boolean = false;
  public exportstatus: string;

  constructor(private filesaver: FileSaverService) {
    super('debug');
  }

  // Lifecycle hooks
  /**
   * Initialiseert de filtertool voor de context 'totaalexport' en bewaart wijzigingen in localStorage.
   */
  async ngOnInit(): Promise<void> {
    try {
      if (!this.api.can('planitem', 'read')) {
        setTimeout(() => this.closeWindow(null, true), 200); //even lang genoeg zodat er niks van alles misgaat met een breedte 0 etc.
        return;
      }
      // Herstel eerder gekozen exportperiode uit localStorage (YYYY-MM-DD strings)
      try {
        const json = localStorage.getItem('totaalexport_daterange');
        if (json) {
          const arr = JSON.parse(json);
          if (Array.isArray(arr) && arr.length === 2 && arr[0] && arr[1]) {
            this.ux.exportDaterange = [new Date(arr[0]), new Date(arr[1])];
          }
        }
      } catch {}

      // Autorisatie: alles-of-niets gate voor filtertool
      this.magFilteren = this.filtertoolFactory.canFiltertool();
      if (this.magFilteren) {
        // Filtertool: verkrijg een instantie via de factory, inclusief eventueel opgeslagen status
        // Context voor dit scherm is expliciet 'totaalexport'
        this.filtertool = this.filtertoolFactory.leesHuidigeFilter('totaalexport');
        if (this.filtertool) {
          // Sla elke boomwijziging direct op via de factory
          if ((this.filtertool as any).boomGewijzigd$) {
            this.subscription(this.filtertool.boomGewijzigd$.subscribe(() => {
              this.filtertoolFactory.saveHuidigeFilter(this.filtertool?.id, 'totaalexport');
            }));
          }
          // Luister naar toepassen van de filtertool; zet de toggle afhankelijk van de boominhoud
          if ((this.filtertool as any).toegepast$) {
            this.subscription(this.filtertool.toegepast$.subscribe(() => {
              /**
               * Bij toepassen bepalen we of de filter inhoud heeft. Als de boom leeg is (geen clausules),
               * dan schakelen we het toepassen uit. Heeft de boom wel kinderen, dan zetten we toepassen aan.
               */
              this.ux.filterActief = !!(this.filtertool?.boom?.kinderen?.length);
            }));
          }
        }
      } else {
        this.filtertool = null;
        this.ux.filterActief = false;
      }
    } catch (e) {
      this.log.error(e);
    }
  }

  // Public methods
  /**
   * Verwerkt het togglen van de filtertoepassing-schakelaar.
   * In dit scherm is geen herbouw nodig; we loggen uitsluitend voor debug en gebruiken de staat bij exporteren.
   */
  public filterActiefGewijzigd(): void {
    this.log.debug(`Filtertoepassing in Totaalexport is nu: ${this.ux?.filterActief ? 'aan' : 'uit'}`);
  }

  /**
   * Geeft aan of de filtertoepassing geactiveerd is. Te gebruiken bij het conditioneel toepassen van filterlogica.
   */
  public get isFilterActief(): boolean {
    return !!this.ux?.filterActief;
  }
  /**
   * Geeft aan of er een actief filter gebruikt wordt in Totaalexport.
   * Een filter geldt als actief als de toggle aan staat en de filterboom
   * van de filtertool ten minste één clausule bevat.
   */
  public get heeftActiefFilter(): boolean {
    return !!(this.isFilterActief && this.filtertool?.boom?.kinderen?.length);
  }

  /**
   * Opent het Filtertoolscherm als afhankelijk venster van dit totaalexportscherm.
   * Het scherm ontvangt de filterId van de bij dit scherm behorende filtertool‑instantie.
   */
  public openFiltertool(): void {
    void this.createWindow('Filtertoolscherm', {filterId: this.filtertool?.id}, true, true);
  }

  // Datumselectie totaalexport
  /**
   * Wordt aangeroepen bij wijziging van de daterangepicker. Slaat de gekozen periode op in localStorage
   * als twee YYYY-MM-DD strings in een array.
   */
  public exportDaterangeGewijzigd(): void {
    try {
      const strings = this.exportRangeStrings();
      if (strings.van && strings.tot) {
        localStorage.setItem('totaalexport_daterange', JSON.stringify([strings.van, strings.tot]));
      } else {
        localStorage.removeItem('totaalexport_daterange');
      }
    } catch {}
  }

  /** Zet snel de exportperiode op de huidige maand. */
  public setDezeMaand(): void {
    const start = moment().startOf('month');
    const eind = moment().endOf('month');
    this.ux.exportDaterange = [start.toDate(), eind.toDate()];
    this.exportDaterangeGewijzigd();
  }
  /** Vorige maand. */
  public setVorigeMaand(): void {
    const start = moment().subtract(1, 'month').startOf('month');
    const eind = moment().subtract(1, 'month').endOf('month');
    this.ux.exportDaterange = [start.toDate(), eind.toDate()];
    this.exportDaterangeGewijzigd();
  }
  /** Dit kwartaal. */
  public setDitKwartaal(): void {
    const start = moment().startOf('quarter');
    const eind = moment().endOf('quarter');
    this.ux.exportDaterange = [start.toDate(), eind.toDate()];
    this.exportDaterangeGewijzigd();
  }
  /** Vorig kwartaal. */
  public setVorigKwartaal(): void {
    const start = moment().subtract(1, 'quarter').startOf('quarter');
    const eind = moment().subtract(1, 'quarter').endOf('quarter');
    this.ux.exportDaterange = [start.toDate(), eind.toDate()];
    this.exportDaterangeGewijzigd();
  }
  /** Dit jaar. */
  public setDitJaar(): void {
    const start = moment().startOf('year');
    const eind = moment().endOf('year');
    this.ux.exportDaterange = [start.toDate(), eind.toDate()];
    this.exportDaterangeGewijzigd();
  }
  /** Vorig jaar. */
  public setVorigJaar(): void {
    const start = moment().subtract(1, 'year').startOf('year');
    const eind = moment().subtract(1, 'year').endOf('year');
    this.ux.exportDaterange = [start.toDate(), eind.toDate()];
    this.exportDaterangeGewijzigd();
  }

  /** Leeg de exportperiode (alles). */
  public setAlles(): void {
    this.ux.exportDaterange = null;
    this.exportDaterangeGewijzigd();
  }

  /**
   * Bepaalt de stringrepresentatie (YYYY-MM-DD) van de gekozen exportperiode. Retourneert lege
   * waarden wanneer de periode niet (volledig) gekozen is.
   */
  private exportRangeStrings(): { van: string | null; tot: string | null } {
    if (!this.ux?.exportDaterange || this.ux.exportDaterange.length !== 2) {
      return { van: null, tot: null };
    }
    const [van, tot] = this.ux.exportDaterange;
    const vans = moment(van).format('YYYY-MM-DD');
    const tots = moment(tot).format('YYYY-MM-DD');
    return { van: vans, tot: tots };
  }

  /**
   * De export-functie
   */
  async exporteer() {

    this.exportstatus = 'Exporteren...';
    //opzet excel
    const workbook = new Excel.Workbook;
    workbook.creator = 'ATRPlan - Slimme Infrasoftware B.V. Apeldoorn';
    //benodigde extra data halen we op in lookups
    const functies: hash<Functie> = await Functie.getLookup(Planitem);
    const personeel: hash<Personeel> = await Personeel.getLookup(undefined, [PersoneelFunctie, PersoneelLabel]); //met relevante subs
    const werkmaatschappijen: hash<Werkmaatschappij> = await Werkmaatschappij.getLookup();

    // Bepaal het exportbereik (YYYY-MM-DD). Van en tot zijn verplicht voor de backend-call.
    const exportRange = this.exportRangeStrings();
    if (!exportRange.van || !exportRange.tot) {
      this.api.toast('Kies eerst een exportperiode.', 'warning');
      this.exportstatus = '';
      return;
    }

    // Haal PPI's en onbezette diensten op via de rol-bewuste backend-methode
    // (placeholder-projecten + overrules al verwerkt)
    const exportdata: {ppis: any[], legeDiensten: any[]} = (await this.api.send('werkbon', 'totaalexport', {
      van: exportRange.van,
      tot: exportRange.tot
    }));

    if (!exportdata) {
      this.log.warn('Geen data ontvangen van backend');
      this.exportstatus = '';
      return;
    }
    const ppis: any[] = exportdata.ppis || [];
    const legeDiensten: any[] = exportdata.legeDiensten || [];
    this.log.debug(`PPI's ontvangen: ${ppis.length}, lege diensten: ${legeDiensten.length}`);

    // Bouw planitem-structuur op uit de PPI-response (analoog aan werkbonnenlijst)
    const planitemMap = new Map<number, Planitem>();
    const projectMap = new Map<number, Project>();

    // ppiRawData: bewaar de ruwe backend-data per ppi-id voor fallback personeelsnaam
    const ppiRawData = new Map<number, any>();

    for (const ppidata of ppis) {
      let planitem = planitemMap.get(ppidata.planitem_id);
      if (!planitem) {
        let project = projectMap.get(ppidata.planitem.project_id);
        if (!project) {
          project = Project.create(ppidata.planitem.project);
          project.planitem = [];
          projectMap.set(ppidata.planitem.project_id, project);
        }
        planitem = Planitem.create(ppidata.planitem, project);
        planitem.personeel_planitem = [];
        project.planitem.push(planitem);
        planitemMap.set(ppidata.planitem_id, planitem);
      }
      const ppi = PersoneelPlanitem.create(ppidata, planitem);
      planitem.personeel_planitem.push(ppi);
      ppiRawData.set(ppi.id, ppidata);
    }

    // Onbezette diensten toevoegen: die hebben per definitie geen PPI's, dus alleen het planitem zelf
    for (const planitemdata of legeDiensten) {
      let project = projectMap.get(planitemdata.project_id);
      if (!project) {
        project = Project.create(planitemdata.project);
        project.planitem = [];
        projectMap.set(planitemdata.project_id, project);
      }
      const planitem = Planitem.create(planitemdata, project);
      planitem.personeel_planitem = [];
      project.planitem.push(planitem);
      planitemMap.set(planitemdata.id, planitem);
    }

    // Lookups koppelen aan planitems
    const leveranciers: hash<Leverancier> = await Leverancier.getLookup(Planitem);
    for (const planitem of planitemMap.values()) {
      planitem.setFunctionlabel(functies);
      planitem.setLeveranciers(leveranciers);
      planitem.setPersoneel(personeel);
      // Fallback: vul personeel in vanuit backend-data voor uitleen-personeel dat niet in de eigen lookup zit
      for (const ppi of planitem.personeel_planitem) {
        if (!ppi.personeel) {
          const raw = ppiRawData.get(ppi.id);
          if (raw?.personeelvast) {
            ppi.personeel = Personeel.create(raw.personeelvast);
          }
        }
      }
    }

    // Planitems gesorteerd ophalen
    let planitems: Planitem[] = sortBy([...planitemMap.values()], ['startdate', 'starttime']);

    //maak de sheets
    const plansheet = workbook.addWorksheet('Diensten', {
      views: [{state: 'frozen', ySplit: 1}]
    });

    plansheet.columns = [
      {header: 'Week', width: 5},
      {header: 'Datum', width: 11},
      {header: 'Begin', width: 7},
      {header: 'Eind', width: 7},
      {header: 'WM', width: 10},
      {header: 'Klant', width: 20},
      {header: 'Project', width: 15},
      {header: 'Projectomschrijving', width: 40},
      {header: 'Capaciteit', width: 10},
      {header: 'Functie', width: 30},
      {header: 'Alias', width: 40},
      {header: 'Extra functies', key: 'extrafuncties', width: 40},
      {header: 'Invullende leverancier', key: 'leverancier', width: 40},
      {header: 'Personeel', key: 'personeel', width: 40},
      {header: 'Telefoon', key: 'telefoon', width: 18},
      {header: 'E-mail', key: 'email', width: 30},
      {header: 'Opmerkingen', key: 'opmerkingen', width: 80},
      {header: 'Aanvraagnummer', width: 15}
    ];

    //eerste rij is bold
    plansheet.getRow(0).font = {bold: true};
    plansheet.getRow(0).alignment = {horizontal: 'center'};
    //en sommige kolommen hebben nog iets speciaals:
    plansheet.getColumn('extrafuncties').alignment = {wrapText: true};
    plansheet.getColumn('personeel').alignment = {wrapText: true};
    plansheet.getColumn('telefoon').alignment = {wrapText: true};
    plansheet.getColumn('email').alignment = {wrapText: true};
    plansheet.getColumn('opmerkingen').alignment = {wrapText: true};

    const werksheet = workbook.addWorksheet('Werkbonnen', {
      views: [{state: 'frozen', ySplit: 1}],
    });

    werksheet.columns = [
      {header: 'Week', width: 5},
      {header: 'Datum', width: 11},
      {header: 'Begin', width: 7},
      {header: 'Eind', width: 7},
      {header: 'WM', width: 10},
      {header: 'Klant', width: 25},
      {header: 'Project', width: 15},
      {header: 'Projectomschrijving', width: 40},
      {header: 'Functie', key: 'functie', width: 30},
      {header: 'Status', width: 20},
      {header: 'Aangemaakt', width: 10},
      {header: 'Gestart', width: 7},
      {header: 'Gestopt', width: 7},
      {header: 'KM Project', width: 10},
      {header: 'KM ww', width: 5},
      {header: 'Pauze', width: 5},
      {header: 'Personeel', width: 40},
      {header: 'Telefoon', width: 18},
      {header: 'E-mail',width: 30},
      {header: 'Groep', width: 20},
      {header: 'Leverancier', key:'leverancier', width: 25},
      {header: 'Ondertekenaar', width: 20},
      {header: 'Bestelnr', width: 20},
      {header: 'Ingediend', width: 10},
      {header: 'Opmerkingen', key: 'opmerkingen', width: 40},
    ];
    werksheet.getColumn('functie').alignment = {wrapText: true};
    werksheet.getColumn('leverancier').alignment = {wrapText: true};
    werksheet.getColumn('opmerkingen').alignment = {wrapText: true};

    let numdiensten = 0;
    let numwerkbonnen = 0;

    for (let item of planitems) {
      // project is al correct (placeholder voor uitleners) via Planitem.create
      const project: Project = item.project;
      let functie: Functie;
      if (item.mainFunction) {
        functie = functies[item.mainFunction];
      }
      const extrafuncties = item.extraFunctions.map(fid => functies[fid]?.naam || '?').join(', ');
      const week = moment(item.startdate).format('YYYY-WW');
      const datum = new Date(item.startdate);
      /**
       * Bepaal de set PPI's (planitem-personeel) die door de volledige filterboom komen.
       * - Bij actief filter gebruiken we de combinatie-evaluatie `filterPPI(pi, persoon)`.
       * - Bij inactief filter nemen we alle PPI's.
       * Op basis hiervan bepalen we of het planitem in "Diensten" wordt opgenomen:
       * - Als er planitem-regels (zonder personeel) matchen óf er zijn gefilterde PPI's, nemen we de rij op.
       * - Anders slaan we het planitem volledig over.
       */
      const ppisGefilterd = this.heeftActiefFilter
        ? item.personeel_planitem.filter(ppi => this.filtertool?.filterPPI(item, ppi.personeel) === true)
        : item.personeel_planitem;

      if (this.heeftActiefFilter) {
        // Gebruik de nieuwe algemene planitem‑evaluatie: neemt planitem‑ en PPI‑context samen.
        // Met legeDienstToestaan blijven onbezette diensten staan zolang de filterboom niet op
        // personeel filtert; een regel op personeel of label laat ze wel afvallen.
        if (!(this.filtertool?.filterPlanitem(item, true) === true)) {
          continue; // sla hele planitem over
        }
      }

      /**
       * De medewerkers voor de personeelskolommen, in vaste volgorde. Normaal zijn dat de
       * gefilterde PPI's; leveren die niets op, dan valt het bij een inactief filter terug op het
       * personeel dat al aan het planitem hangt. Naam, telefoon en e-mail worden hier alle drie
       * uit opgebouwd, zodat de regels in die kolommen bij dezelfde medewerker horen.
       */
      const medewerkers: Personeel[] = ppisGefilterd.length ? ppisGefilterd.map(ppi => ppi.personeel) :
        (this.heeftActiefFilter ? [] : item.personeel);

      /**
       * Schrijf rij naar "Diensten". De personeelskolommen zijn gebaseerd op de gefilterde PPI's,
       * zodat alleen medewerkers die door de volledige boom komen worden getoond.
       */
      plansheet.addRow([
        week,
        datum,
        item.starttime,
        item.endtime,
        project ? werkmaatschappijen[project.werkmaatschappij_id]?.afkorting : '',
        project?.klantnaam,
        project?.naam,
        project?.omschrijving,
        item.capacity,
        functie?.naam,
        item.functionalias,
        extrafuncties,
        item.leverancier_id ? leveranciers[item.leverancier_id]?.naam : null,
        medewerkers.map(p => p?.naam || '?').join('\r'),
        medewerkers.map(p => p?.telefoonnummer || '').join('\r'),
        medewerkers.map(p => p?.email || '').join('\r'),
        item.opmerkingen,
        item.aanvraagnummer
      ]);
      numdiensten++;
      /**
       * Schrijf rijen naar "Werkbonnen". Neem alleen PPI's mee die door de volledige boom komen.
       */
      for (let ppi of ppisGefilterd) {
        let status: string;
        if (! ppi.werkbon)
        {
          status = 'Ontbreekt';
        }
        else if (ppi.werkbon.status === 'ingediend')
        {
          status = ppi.werkbon.proforma ? 'Ongetekend' : 'Getekend';
        }
        else {
          status = ppi.werkbon.status;
        }
        const raw = ppiRawData.get(ppi.id);
        // Alleen op een in-/uitleendienst staat personeel van een andere werkmaatschappij; daar
        // tonen we die werkmaatschappij. Zonder externe werkmaatschappij gaat het om personeel dat
        // inmiddels verhuisd is, en geldt gewoon de leverancier van de PPI.
        const isIngeleend = !!item.externe_werkmaatschappij && raw?.personeelvast && !personeel[ppi.personeelvast_id];
        const leverancierKolom = isIngeleend
          ? `Werkmaatschappij: ${raw.personeelvast.werkmaatschappij?.afkorting ?? ''}`
          : (ppi.personeel?.groep !== 'Personeel' ? ppi.leverancier?.naam : '');

        werksheet.addRow([
          week,
          datum,
          item.starttime,
          item.endtime,
          project ? werkmaatschappijen[project.werkmaatschappij_id]?.afkorting : '',
          project?.klantnaam,
          project?.naam,
          project?.omschrijving,
          item.functionlabel,
          status,
          ppi.werkbon ? new Date(ppi.werkbon.createdAt) : "",
          ppi.werkbon?.starttijd,
          ppi.werkbon?.eindtijd,
          ppi.werkbon?.kilometers,
          ppi.werkbon?.kilometers_ww,
          ppi.werkbon?.pauze,
          ppi.personeel?.naam,
          ppi.personeel?.telefoonnummer,
          ppi.personeel?.email,
          ppi.personeel?.groep,
          leverancierKolom,
          ppi.werkbon?.ondertekenaar,
          ppi.inkooporder,
          ppi.ingediend,
          ppi.werkbon?.opmerkingen
        ]);
        numwerkbonnen++;
      }
    }

    ////// opslaan excel
    const buffer = await workbook.xlsx.writeBuffer();
    this.filesaver.save(new Blob([buffer]), `ATRPlan-Totaalexport ${moment().format('YYYY-MM-DD')}.xlsx`);
    this.api.toast('Het Excel-bestand is door je browser gedownload. Je vindt het rechtsboven of in je downloadmap.', 'success');
    this.exportstatus = `Export is voltooid. ${numdiensten} diensten en ${numwerkbonnen} werkbonnen`;
  }
}
